import { useGetV1Notifications } from "@solarkey/api-client";
import { Link, useNavigate } from "@tanstack/react-router";
import { Bell } from "lucide-react";
import { useEffect, useRef, useState } from "react";

/**
 * NotificationCentre (UX-004 Shell) — bell trigger with an unread count and a
 * dropdown backed by GET /v1/notifications. Empty is a first-class state (the
 * notification engine is a Build-01 seam). Closes on outside click / Escape.
 */
export function NotificationCentre(): React.ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const query = useGetV1Notifications(undefined, { query: { enabled: open } });
  const items = query.data?.status === 200 ? (query.data.data.data.items ?? []) : [];
  const unread = items.filter((n) => n.read === false).length;

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) {
      document.addEventListener("mousedown", onDoc);
      document.addEventListener("keydown", onKey);
    }
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        aria-expanded={open}
        className="relative rounded border border-border p-1 text-text-muted focus:outline focus:outline-2 focus:outline-accent"
      >
        <Bell aria-hidden size={16} />
        {unread > 0 ? (
          <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-accent px-1 text-center text-[10px] leading-4 text-accent-contrast">
            {unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-1 w-80 rounded border border-border bg-surface"
          style={{ boxShadow: "var(--sk-shadow-float)" }}
        >
          <div className="flex items-baseline gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            <span className="text-xs uppercase tracking-wide text-text-muted">
              {unread > 0 ? `${unread} unread` : "all read"}
            </span>
          </div>
          <ul className="max-h-80 overflow-auto p-1">
            {query.isLoading ? (
              <li className="px-3 py-2 text-sm text-text-muted">Loading…</li>
            ) : items.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-text-muted">Nothing new.</li>
            ) : (
              items.map((n, i) => {
                const route = n.record_route ?? undefined;
                const body = (
                  <>
                    <p className={n.read ? "text-text-muted" : ""}>{n.text}</p>
                    {n.created_at ? (
                      <time className="text-xs text-text-muted" dateTime={n.created_at}>
                        {new Date(n.created_at).toLocaleString()}
                      </time>
                    ) : null}
                  </>
                );
                return (
                  <li key={n.id ?? i}>
                    {route ? (
                      <a
                        href={route}
                        onClick={(e) => {
                          e.preventDefault();
                          setOpen(false);
                          void navigate({ to: route as never });
                        }}
                        className="block rounded px-3 py-2 hover:bg-surface-raised focus:bg-surface-raised focus:outline-none"
                      >
                        {body}
                      </a>
                    ) : (
                      <div className="rounded px-3 py-2">{body}</div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
          <div className="border-t border-border p-1">
            {/* A real link, so the href is where the click goes. It used to carry
                href="/t/_/s/_/notifications" — not even a resolvable URL — while the
                handler navigated to /preferences, so middle-click and left-click
                landed on different screens. Notification policy is edited on
                Preferences, which is where both now go. */}
            <Link
              to="/preferences"
              onClick={() => setOpen(false)}
              className="block rounded px-3 py-2 text-sm text-accent hover:bg-surface-raised focus:bg-surface-raised focus:outline-none"
            >
              Notification settings
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
